import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../SEO/SEO';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { TrendingUp, Target, Shield, CheckCircle2, ArrowRight, BarChart3, Users, FileText, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import './InvestorInfo.css';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

const sanitizeAndFormatHtml = (content) => {
  if (!content || typeof content !== 'string') return '';
  let html = content;
  html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
    const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
    if (!attrMatch) return tag;
    const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
    const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
    const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
    return `<a href="${rawUrl}"${targetAttr}>`;
  });
  if (!/<(?:br|p|div)\b/i.test(html)) {
    html = html.replace(/\r?\n/g, '<br />');
  }
  return html;
};

const InvestorInfo = () => {
  const navigate = useNavigate();

  const handleRichTextClick = (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    const linkTarget = target.getAttribute('target');
    if (!href || linkTarget === '_blank') return;

    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('https://ahamhfc.com/') || href.startsWith('http://ahamhfc.com/')) {
      const path = href.replace(/^https?:\/\/ahamhfc\.com/, '');
      if (path.startsWith('/')) {
        e.preventDefault();
        navigate(path);
      }
    }
  };

  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [foundationSettings, setFoundationSettings] = useState(null);
  const [visionPoints, setVisionPoints] = useState([]);
  const [progressSettings, setProgressSettings] = useState(null);
  const [progressHighlights, setProgressHighlights] = useState([]);
  const [trustSettings, setTrustSettings] = useState(null);
  const [governanceHighlights, setGovernanceHighlights] = useState([]);
  const [ctaSettings, setCtaSettings] = useState(null);

  // Fetch banner data
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get('/api/admin/banners/page/investor_info');
        if (response.data.success && response.data.data) {
          const banners = response.data.data;
          if (Array.isArray(banners) && banners.length > 0) {
            setBanner(banners[0]);
          } else if (!Array.isArray(banners)) {
            setBanner(banners);
          }
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBanner();
  }, []);

  // Fetch foundation settings and vision points
  useEffect(() => {
    const fetchFoundationData = async () => {
      try {
        const [settingsRes, pointsRes] = await Promise.all([
          axios.get('/api/admin/investor-foundation/settings'),
          axios.get('/api/admin/investor-foundation/points/active')
        ]);

        if (settingsRes.data.success) {
          setFoundationSettings(settingsRes.data.data);
        }
        if (pointsRes.data.success) {
          setVisionPoints(pointsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching foundation data:', error);
      }
    };
    fetchFoundationData();
  }, []);

  // Icon mapping for vision points
  const getIcon = (iconName) => {
    const iconMap = {
      'Target': <Target className="w-5 h-5" />,
      'BarChart3': <BarChart3 className="w-5 h-5" />,
      'Users': <Users className="w-5 h-5" />,
      'TrendingUp': <TrendingUp className="w-5 h-5" />,
      'Shield': <Shield className="w-5 h-5" />,
      'CheckCircle2': <CheckCircle2 className="w-5 h-5" />,
      'Star': <CheckCircle2 className="w-5 h-5" />,
      'Zap': <CheckCircle2 className="w-5 h-5" />,
      'Award': <CheckCircle2 className="w-5 h-5" />,
      'Heart': <CheckCircle2 className="w-5 h-5" />
    };
    return iconMap[iconName] || <Target className="w-5 h-5" />;
  };

  // Fetch progress settings and highlights
  useEffect(() => {
    const fetchProgressData = async () => {
      try {
        const [settingsRes, highlightsRes] = await Promise.all([
          axios.get('/api/admin/investor-progress/settings'),
          axios.get('/api/admin/investor-progress/highlights/active')
        ]);

        if (settingsRes.data.success) {
          setProgressSettings(settingsRes.data.data);
        }
        if (highlightsRes.data.success) {
          setProgressHighlights(highlightsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching progress data:', error);
      }
    };
    fetchProgressData();
  }, []);

  // Fetch trust settings and governance highlights
  useEffect(() => {
    const fetchTrustData = async () => {
      try {
        const [settingsRes, highlightsRes] = await Promise.all([
          axios.get('/api/admin/investor-trust/settings'),
          axios.get('/api/admin/investor-trust/highlights/active')
        ]);

        if (settingsRes.data.success) {
          setTrustSettings(settingsRes.data.data);
        }
        if (highlightsRes.data.success) {
          setGovernanceHighlights(highlightsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching trust data:', error);
      }
    };
    fetchTrustData();
  }, []);

  // Fetch CTA data
  useEffect(() => {
    const fetchCtaData = async () => {
      try {
        const response = await axios.get('/api/admin/cta-banner/settings/investor_info');
        if (response.data.success) {
          setCtaSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA data:', error);
      }
    };
    fetchCtaData();
  }, []);

  const performanceHighlights = [
    'Stable year-on-year operational expansion',
    'Strengthened product & technology foundation',
    'Scalable processes to support future growth'
  ];



  return (
    <div className="investor-info">

      <SEO
        title="Aham Housing Finance Investor Information & Reports"
        description="Access Aham Housing Finance investor information, credit ratings, financial reports and governance disclosures for lenders, partners and stakeholders."
        keywords="Aham Housing Finance investor info, Aham Housing Finance investor relations, Aham investor information, Aham Housing Finance financial disclosures, Aham Housing Finance credit rating, India Ratings Aham Housing Finance, CRISIL rating Aham Housing Finance, CARE Ratings Aham Housing Finance, Aham Housing Finance capital adequacy, Aham Housing Finance funding rounds, Aham Housing Finance investors, Aham Housing Finance equity base, Aham Housing Finance leverage, Aham Housing Finance AUM, Aham Housing Finance annual reports, Aham Housing Finance governance, housing finance investor relations India, NBFC investor information, HFC financial performance, Aham Housing Finance rating rationale"
        canonical="https://ahamhfc.com/investor-information"
        ogTitle="Aham Housing Finance Investor Information & Reports"
        ogDescription="Access Aham Housing Finance investor information, credit ratings, financial reports and governance disclosures for lenders, partners and stakeholders."
      />

      {/* Hero Banner */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${banner && banner.image_url ? banner.image_url : '/img/Banners/Investor-Banner.webp'}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-white/80 font-semibold tracking-wider uppercase mb-2 text-sm md:text-base">Investor Information</div>
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {loading ? (
                'Transparent growth.\nStrong foundations.'
              ) : (
                banner?.title || 'Transparent growth.\nStrong foundations.'
              )}
            </h1>
            <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-6 md:mb-8">
              {banner?.subtitle || 'A future we build with confidence.'}
            </p>
            {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Investor Info</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 1: Vision & Purpose */}
      <section className="vision-section">
        <div className="section-container">
          <div className="vision-layout">
            <div className="vision-content text-center lg:text-left">
              <div className="section-header text-center lg:text-left">
                <span className="section-tag">{foundationSettings?.section_tag || 'Our Foundation'}</span>
                <h2 className="section-heading mx-auto lg:mx-0">{foundationSettings?.section_heading || 'Building Value With Purpose'}</h2>
              </div>

              <div className="vision-text">
                <p className="lead-text">
                  {foundationSettings?.lead_text || 'At Aham, our growth is driven by clarity of purpose and long-term thinking. We focus on creating real solutions, strong relationships, and sustainable impact across every milestone.'}
                </p>
                <p className="secondary-text">
                  {foundationSettings?.secondary_text || 'For us, success is not just about numbers — it is about meaningful value and trust built over time.'}
                </p>
              </div>


              <div className="key-points-grid">
                {visionPoints.map((point, index) => (
                  <div key={index} className="key-point-card">
                    <div className="key-point-icon">{getIcon(point.icon_name)}</div>
                    <p className="key-point-text">{point.point_text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="vision-visual">
              <div className="visual-card primary">
                <img
                  src={`${foundationSettings?.primary_image_url || 'img/Investor/Building-growth.webp'}`}
                  alt="Aham Housing Finance investor information page with financial reports, credit ratings and corporate governance details for stakeholders"
                  className="visual-image"
                />
                <div className="visual-badge">
                  <TrendingUp className="w-5 h-5" />
                  <span>{foundationSettings?.primary_image_badge || 'Sustainable Growth'}</span>
                </div>
              </div>
              <div className="visual-card secondary">
                <img
                  src={`${foundationSettings?.secondary_image_url || 'img/Investor/Business-analytics.webp'}`}
                  alt="Aham Housing Finance investor information page with financial reports, credit ratings and corporate governance details for stakeholders"
                  className="visual-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Performance & Progress */}
      <section className="performance-section">
        <div className="section-container">
          <div className="performance-layout">
            <div className="performance-visual">
              <img
                src={`${progressSettings?.image_url || 'img/Investor/Performance-metrics.webp'}`}
                alt="Aham Housing Finance investor information page with financial reports, credit ratings and corporate governance details for stakeholders"
                className="performance-image"
              />
              <div className="performance-overlay">
                <div className="overlay-stat">
                  <TrendingUp className="overlay-icon" />
                  <div className="overlay-text">
                    <div className="overlay-value">{progressSettings?.overlay_value || 'Driven by market insights.'}</div>
                    <div className="overlay-label">{progressSettings?.overlay_label || 'Built for long-term advantage.'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="performance-content text-center lg:text-left">
              <div className="section-header text-center lg:text-left">
                <span className="section-tag">{progressSettings?.section_tag || 'Our Progress'}</span>
                <h2 className="section-heading mx-auto lg:mx-0">{progressSettings?.section_heading || 'Consistent Performance, Measured Progress'}</h2>
              </div>

              <p className="lead-text">
                {progressSettings?.lead_text || 'We believe in measurable progress backed by transparent reporting. Our performance reflects our commitment to efficiency, responsible investments, and continuous improvement.'}
              </p>

              <Card className="highlights-card">
                <h3 className="highlights-title">Key Highlights</h3>
                <ul className="highlights-list">
                  {progressHighlights.map((highlight, index) => (
                    <li key={index} className="highlight-item">
                      <CheckCircle2 className="highlight-icon" />
                      <span>{highlight.highlight_text}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Governance & Trust */}
      <section className="governance-section">
        <div className="section-container">
          <div className="governance-header-center">
            <span className="section-tag">{trustSettings?.section_tag || 'Trust & Integrity'}</span>
            <h2 className="section-heading">{trustSettings?.section_heading || 'Responsible Governance, Strong Ethics'}</h2>
            <p className="section-intro">
              {trustSettings?.lead_text || 'Our governance approach ensures that every decision aligns with our values, regulatory standards, and long-term vision. We maintain clarity, compliance, and responsibility across our leadership and operational model.'}
            </p>
          </div>

          <div className="governance-content">
            <div className="governance-visual">
              <img
                src={`${trustSettings?.image_url || 'img/Investor/Corporate-governance.webp'}`}
                alt="Aham Housing Finance investor information page with financial reports, credit ratings and corporate governance details for stakeholders"
                className="governance-image"
              />
            </div>

            <div className="governance-cards">
              {governanceHighlights.map((item, index) => (
                <Card key={index} className="governance-card">
                  <Shield className="governance-icon" />
                  <p className="governance-text">{item.highlight_text}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Credit Rating Certificates */}
      <section className="investor-rating-section">
        <div className="section-container">
          <div className="investor-rating-header">
            <span className="section-tag">{trustSettings?.credit_rating_tag || 'Rating & Accreditation'}</span>
            <h2 className="section-heading">{trustSettings?.credit_rating_title || 'Credit Rating Certificates'}</h2>
          </div>

          <div className="investor-rating-box" onClick={handleRichTextClick}>
            <Award className="investor-rating-icon" />
            <div className="investor-rating-content">
              <p
                dangerouslySetInnerHTML={{
                  __html: sanitizeAndFormatHtml(
                    trustSettings?.credit_rating_text ||
                    'View Aham Housing Finance\'s latest <a href="https://ahamhfc.com/aham-housing-finance-credit-rating-certificate">credit rating certificates</a> and related rating information.'
                  )
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      {/* <section className="investor-cta">
        <div className="cta-content-box">
          <div className="cta-icon-wrapper">
            <Users className="cta-icon" />
          </div>
          <h2 className="cta-heading">{ctaSettings?.title || "We welcome investors who believe in responsible growth and long-term partnership."}</h2>
          <p className="cta-subtext">{ctaSettings?.description || "Want to learn more or connect with our investor relations team? Let's start a conversation."}</p>
          <div className="cta-action-buttons">
            <Button
              variant="outline"
              className="cta-btn-outline"
              onClick={() => {
                if (ctaSettings?.button_link) {
                  if (ctaSettings.button_link.startsWith('http')) {
                    window.location.href = ctaSettings.button_link;
                  } else {
                    navigate(ctaSettings.button_link);
                  }
                } else {
                  navigate('/contact');
                }
              }}>
              {ctaSettings?.button_text || 'Contact'}
            </Button>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default InvestorInfo;